// Deterministic dashboard engine: profiling, filtering, aggregation and formatting.
// Every number shown on a dashboard tile must come from these functions applied to real rows.

export type ColumnType = "number" | "date" | "category" | "text";
export type Aggregation = "sum" | "avg" | "count" | "min" | "max" | "distinct";
export type TileType =
  | "kpi" | "bar" | "stacked" | "line" | "area" | "combo"
  | "pie" | "scatter" | "funnel" | "gauge" | "table" | "heatmap";
export type DateGrain = "day" | "week" | "month" | "quarter" | "year";

export interface ColumnProfile {
  name: string;
  type: ColumnType;
  distinct: number;
  nulls: number;
}

export interface TileSpec {
  id: string;
  type: TileType;
  title: string;
  dimension?: string;     // x / category
  measure?: string;       // y
  measure2?: string;      // combo line / scatter y / heatmap value
  series?: string;        // stacked breakdown / heatmap columns
  aggregation: Aggregation;
  grain?: DateGrain;
  topN?: number;
  sort?: "value-desc" | "value-asc" | "label";
  target?: number;        // gauge target
  size: 1 | 2 | 3;        // grid columns spanned
}

export interface DashboardFilters {
  dateColumn?: string;
  from?: string; // yyyy-mm-dd
  to?: string;
  equals: Record<string, string[]>; // column -> allowed values (dropdown + cross-filter)
}

const ID_LIKE = /(^id$|_id$|^id_|code$|zip|postal|phone|^year$)/i;

export const toNumber = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const cleaned = String(v).replace(/[,\s$€£₦%]/g, "");
  if (cleaned === "" || cleaned === "-") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

export const toDate = (v: unknown): Date | null => {
  if (v === null || v === undefined || v === "") return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  const s = String(v).trim();
  if (/^\d+(\.\d+)?$/.test(s)) return null; // plain numbers are not dates
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

export function profileColumns(rows: Record<string, any>[]): ColumnProfile[] {
  if (!rows.length) return [];
  const names = Array.from(new Set(rows.slice(0, 200).flatMap((r) => Object.keys(r))));
  const sample = rows.slice(0, 1000);
  return names.map((name) => {
    const vals = sample.map((r) => r[name]).filter((v) => v !== null && v !== undefined && String(v).trim() !== "");
    const nulls = sample.length - vals.length;
    const distinct = new Set(vals.map(String)).size;
    const numShare = vals.filter((v) => toNumber(v) !== null).length / (vals.length || 1);
    const dateShare = vals.filter((v) => toDate(v) !== null).length / (vals.length || 1);
    let type: ColumnType;
    if (numShare >= 0.9 && !ID_LIKE.test(name)) type = "number";
    else if (dateShare >= 0.9) type = "date";
    else if (distinct <= Math.max(30, vals.length * 0.5)) type = "category";
    else type = "text";
    return { name, type, distinct, nulls };
  });
}

const iso = (d: Date) => d.toISOString().slice(0, 10);

export function applyFilters(
  rows: Record<string, any>[],
  f: DashboardFilters,
  exclude?: string,
): Record<string, any>[] {
  const fromT = f.from ? new Date(f.from).getTime() : null;
  const toT = f.to ? new Date(f.to).getTime() + 86_399_999 : null;
  const eqEntries = Object.entries(f.equals).filter(([c, v]) => c !== exclude && v.length);
  return rows.filter((r) => {
    if (f.dateColumn && (fromT !== null || toT !== null)) {
      const d = toDate(r[f.dateColumn]);
      if (!d) return false;
      const t = d.getTime();
      if (fromT !== null && t < fromT) return false;
      if (toT !== null && t > toT) return false;
    }
    return eqEntries.every(([c, allowed]) => allowed.includes(String(r[c] ?? "")));
  });
}

/** Same-length window immediately before the selected range, for KPI comparisons. */
export function previousPeriodFilters(f: DashboardFilters): DashboardFilters | null {
  if (!f.dateColumn || !f.from || !f.to) return null;
  const from = new Date(f.from);
  const to = new Date(f.to);
  const span = to.getTime() - from.getTime() + 86_400_000;
  return {
    ...f,
    from: iso(new Date(from.getTime() - span)),
    to: iso(new Date(from.getTime() - 86_400_000)),
  };
}

export function aggregate(values: unknown[], agg: Aggregation): number | null {
  if (agg === "count") return values.length;
  if (agg === "distinct") return new Set(values.map(String)).size;
  const nums = values.map(toNumber).filter((n): n is number => n !== null);
  if (!nums.length) return null;
  switch (agg) {
    case "sum": return nums.reduce((a, b) => a + b, 0);
    case "avg": return nums.reduce((a, b) => a + b, 0) / nums.length;
    case "min": return Math.min(...nums);
    case "max": return Math.max(...nums);
  }
  return null;
}

export function bucketDate(d: Date, grain: DateGrain): string {
  const y = d.getFullYear();
  const m = d.getMonth();
  switch (grain) {
    case "day": return iso(d);
    case "week": {
      const t = new Date(Date.UTC(y, m, d.getDate()));
      t.setUTCDate(t.getUTCDate() - ((t.getUTCDay() + 6) % 7));
      return iso(t);
    }
    case "month": return `${y}-${String(m + 1).padStart(2, "0")}`;
    case "quarter": return `${y}-Q${Math.floor(m / 3) + 1}`;
    case "year": return String(y);
  }
}

const keyOf = (row: Record<string, any>, col: string, profiles: ColumnProfile[], grain?: DateGrain) => {
  const p = profiles.find((c) => c.name === col);
  if (p?.type === "date") {
    const d = toDate(row[col]);
    return d ? bucketDate(d, grain ?? "month") : null;
  }
  const v = row[col];
  return v === null || v === undefined || String(v).trim() === "" ? "(blank)" : String(v);
};

export interface GroupedPoint { name: string; value: number; value2?: number; [series: string]: any }

/** Group rows by a dimension (and optional series) and aggregate measures. */
export function groupBy(
  rows: Record<string, any>[],
  spec: TileSpec,
  profiles: ColumnProfile[],
): { points: GroupedPoint[]; seriesKeys: string[] } {
  if (!spec.dimension) return { points: [], seriesKeys: [] };
  const groups = new Map<string, Record<string, any>[]>();
  for (const r of rows) {
    const k = keyOf(r, spec.dimension, profiles, spec.grain);
    if (k === null) continue;
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k)!.push(r);
  }
  const measureVals = (rs: Record<string, any>[], m?: string) =>
    aggregate(m ? rs.map((r) => r[m]) : rs, m ? spec.aggregation : "count") ?? 0;

  let seriesKeys: string[] = [];
  if (spec.series) {
    const totals = new Map<string, number>();
    rows.forEach((r) => {
      const s = String(r[spec.series!] ?? "(blank)");
      totals.set(s, (totals.get(s) ?? 0) + 1);
    });
    seriesKeys = [...totals.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8).map(([s]) => s);
  }

  let points: GroupedPoint[] = [...groups.entries()].map(([name, rs]) => {
    const p: GroupedPoint = { name, value: measureVals(rs, spec.measure) };
    if (spec.measure2) p.value2 = aggregate(rs.map((r) => r[spec.measure2!]), spec.aggregation) ?? 0;
    for (const s of seriesKeys) {
      p[s] = measureVals(rs.filter((r) => String(r[spec.series!] ?? "(blank)") === s), spec.measure);
    }
    return p;
  });

  const isDate = profiles.find((c) => c.name === spec.dimension)?.type === "date";
  const sort = isDate ? "label" : spec.sort ?? "value-desc";
  points.sort((a, b) =>
    sort === "label" ? a.name.localeCompare(b.name, undefined, { numeric: true })
      : sort === "value-asc" ? a.value - b.value : b.value - a.value,
  );
  if (!isDate && spec.topN && points.length > spec.topN) points = points.slice(0, spec.topN);
  return { points, seriesKeys };
}

export const MONEY_HINT = /(revenue|sales|price|amount|cost|profit|income|spend|value|total|budget|fee)/i;
export const PERCENT_HINT = /(rate|percent|pct|ratio|margin|share|%)/i;

export function formatValue(n: number | null | undefined, column?: string, compact = true): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  if (column && PERCENT_HINT.test(column) && Math.abs(n) <= 100) {
    return `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
  }
  const opts: Intl.NumberFormatOptions = compact && Math.abs(n) >= 10_000
    ? { notation: "compact", maximumFractionDigits: 1 }
    : { maximumFractionDigits: Math.abs(n) < 100 ? 2 : 0 };
  return n.toLocaleString(undefined, opts);
}

export const aggLabel: Record<Aggregation, string> = {
  sum: "Total", avg: "Average", count: "Count", min: "Minimum", max: "Maximum", distinct: "Unique",
};

let seq = 0;
export const newTileId = () => `t${Date.now().toString(36)}${(seq++).toString(36)}`;

/** Deterministic starter layout chosen from column types (no AI, no sample data). */
export function autoLayout(profiles: ColumnProfile[]): TileSpec[] {
  const nums = profiles.filter((p) => p.type === "number");
  const dates = profiles.filter((p) => p.type === "date");
  const cats = profiles.filter((p) => p.type === "category" && p.distinct > 1).sort((a, b) => a.distinct - b.distinct);
  const tiles: TileSpec[] = [];
  const mainAgg = (m?: ColumnProfile): Aggregation => (m && PERCENT_HINT.test(m.name) ? "avg" : "sum");

  tiles.push({ id: newTileId(), type: "kpi", title: "Records", aggregation: "count", size: 1 });
  nums.slice(0, 3).forEach((m) =>
    tiles.push({ id: newTileId(), type: "kpi", title: `${aggLabel[mainAgg(m)]} ${m.name}`, measure: m.name, aggregation: mainAgg(m), size: 1 }),
  );
  const m0 = nums[0];
  if (dates[0]) {
    tiles.push({
      id: newTileId(), type: nums[1] ? "combo" : "area",
      title: m0 ? `${m0.name} over time` : "Records over time",
      dimension: dates[0].name, measure: m0?.name, measure2: nums[1]?.name,
      aggregation: mainAgg(m0), grain: "month", size: 3,
    });
  }
  if (cats[0]) {
    tiles.push({
      id: newTileId(), type: "bar", title: `${m0 ? m0.name : "Records"} by ${cats[0].name}`,
      dimension: cats[0].name, measure: m0?.name, aggregation: m0 ? mainAgg(m0) : "count", topN: 10, size: 2,
    });
    tiles.push({
      id: newTileId(), type: cats[0].distinct <= 6 ? "pie" : "funnel", title: `Share by ${cats[0].name}`,
      dimension: cats[0].name, measure: m0?.name, aggregation: m0 ? mainAgg(m0) : "count", topN: 6, size: 1,
    });
  }
  if (cats[0] && cats[1]) {
    tiles.push({
      id: newTileId(), type: "heatmap", title: `${cats[0].name} × ${cats[1].name}`,
      dimension: cats[0].name, series: cats[1].name, measure: m0?.name,
      aggregation: m0 ? mainAgg(m0) : "count", topN: 10, size: 3,
    });
  }
  if (nums.length >= 2) {
    tiles.push({
      id: newTileId(), type: "scatter", title: `${nums[0].name} vs ${nums[1].name}`,
      measure: nums[0].name, measure2: nums[1].name, aggregation: "sum", size: 2,
    });
  }
  tiles.push({ id: newTileId(), type: "table", title: "Detail rows", aggregation: "count", size: nums.length >= 2 ? 1 : 3 });
  return tiles;
}
